/**
 * How the "processing" phase of a video upload is presented. (KAN-532)
 *
 * This replaces `startProcessingSimulation()`, a setInterval that ramped a
 * number on a curve and then held at 98 "until actual completion". That number
 * was never connected to anything Bunny was doing: it read 98% on a video that
 * had not begun encoding, and it read 98% just the same on a video whose
 * status channel had gone silent. The card's test criteria require the
 * percentage to track the real encode and never park at a number implying
 * "almost done".
 *
 * Bunny's queue is outside our control — the reporter's video sat Queued for
 * 26m35s and then encoded in 50s — so the rule here is that the UI survives a
 * long wait honestly. There is no client-side timeout: nothing in this module
 * can turn "slow" into "failed".
 *
 * @requirements
 * - REQ-VIDEO-102: Video record tracks processing through to ready
 */

export interface ProcessingStatusLike {
  status?: string;
  isReady?: boolean;
  isProcessing?: boolean;
  hasError?: boolean;
  /** Set by BunnyVideoStatusService when the status could not be READ. */
  checkFailed?: boolean;
  encodeProgress?: number;
  statusMessage?: string;
}

export interface ProcessingView {
  /** 0-100. Only ever 100 when the video is genuinely ready. */
  progress: number;
  message: string;
  /** True when there is no honest number to show — render a spinner. */
  indeterminate: boolean;
  /** True ONLY for a real Bunny encode failure. */
  isFailure: boolean;
}

export const QUEUED_MESSAGE = 'Waiting for the video service to start encoding';
export const CHECK_FAILED_MESSAGE = 'Still processing - we will keep checking';
export const READY_MESSAGE = 'Video is ready';
export const ENCODING_MESSAGE = 'Encoding your video';

/** Encoding is done at 100, but readiness is a CDN fact (KAN-507). Hold at 99. */
const MAX_PROGRESS_BEFORE_READY = 99;

const BASE_POLL_MS = 10000;
const MAX_POLL_MS = 120000;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Turn one status poll into what the instructor sees.
 *
 * `previousProgress` makes the bar monotonic: a poll that answers with a lower
 * encodeProgress (or cannot answer at all) must not walk the bar backwards.
 */
export function deriveProcessingView(
  status: ProcessingStatusLike | null | undefined,
  previousProgress = 0
): ProcessingView {
  const floor = clamp(Number.isFinite(previousProgress) ? previousProgress : 0, 0, 100);

  if (!status) {
    return { progress: floor, message: QUEUED_MESSAGE, indeterminate: floor <= 0, isFailure: false };
  }

  // A failure to READ the status is not a failure of the video. This is the
  // distinction KAN-532 exists for: the old code collapsed both into
  // `hasError` and reported "Video processing failed on Bunny CDN".
  if (status.checkFailed) {
    return { progress: floor, message: CHECK_FAILED_MESSAGE, indeterminate: floor <= 0, isFailure: false };
  }

  if (status.hasError) {
    return {
      progress: floor,
      message: status.statusMessage || 'Video processing failed',
      indeterminate: false,
      isFailure: true
    };
  }

  if (status.isReady) {
    return { progress: 100, message: READY_MESSAGE, indeterminate: false, isFailure: false };
  }

  const reported = Number.isFinite(status.encodeProgress) ? Number(status.encodeProgress) : 0;
  const progress = clamp(Math.max(reported, floor), 0, MAX_PROGRESS_BEFORE_READY);

  if (progress <= 0) {
    return { progress: 0, message: QUEUED_MESSAGE, indeterminate: true, isFailure: false };
  }

  return {
    progress,
    message: status.statusMessage || ENCODING_MESSAGE,
    indeterminate: false,
    isFailure: false
  };
}

/**
 * How long to wait before the next status poll.
 *
 * Backs off after consecutive failed CHECKS so a wobbling endpoint is not
 * hammered, but keeps a ceiling: a video sitting in Bunny's queue for half an
 * hour must still be looked at periodically. Never returns Infinity — giving
 * up is what produced the false failure.
 */
export function nextStatusPollDelayMs(consecutiveCheckFailures: number): number {
  const failures = Math.max(0, Math.floor(consecutiveCheckFailures || 0));
  return Math.min(MAX_POLL_MS, BASE_POLL_MS * Math.pow(2, failures));
}

// ===== STALL DETECTION (KAN-579, D2) =====
//
// The poll above had no deadline, so a record Bunny never encodes was polled
// for as long as the tab stayed open. A stall is a SUCCESSFUL status read that
// shows the same `status|encodeProgress` fingerprint it showed 45 minutes ago.
//
// Two things a stall is deliberately NOT:
// - Proof that the file never arrived. Round 1 of this card deleted the record
//   on a `storageSize === 0` reading and destroyed live uploads, because Bunny
//   publishes no byte count until encoding finishes (0 of 53 queued videos
//   report a size; 195 of 195 finished ones do). A stall therefore only stops
//   the poll and tells the instructor. Nothing here deletes or fails a row.
// - A judgement about a failed CHECK. An unreadable status says nothing about
//   the video (KAN-532), so it neither advances nor restarts the clock.
//
// The window restarts whenever the fingerprint moves, so a multi-hour encode
// of a 2GB file whose progress keeps ticking never stalls. 45 minutes clears
// the longest healthy queue wait measured on a real upload (26m35s) with room.

/** How long an unchanged, successfully-read status stands before it is a stall. */
export const PROCESSING_STALL_MS = 45 * 60 * 1000;

export const STALLED_MESSAGE =
  'The video service has reported no progress for 45 minutes. Your upload has not been removed and may still finish in the background.';

export interface StallClock {
  /** `status|encodeProgress` of the last successful reading. */
  fingerprint: string;
  /** When that fingerprint was first seen (ms epoch). */
  since: number;
}

export interface StallReading {
  clock: StallClock;
  stalled: boolean;
}

function stallFingerprint(status: ProcessingStatusLike): string {
  const reported = Number.isFinite(status.encodeProgress) ? Number(status.encodeProgress) : 0;
  return `${status.status ?? 'unknown'}|${reported}`;
}

/**
 * Fold one status poll into the stall clock.
 *
 * `clock` is null before the first successful reading. A failed check returns
 * the clock unchanged and never reports a stall.
 */
export function trackProcessingStall(
  status: ProcessingStatusLike | null | undefined,
  clock: StallClock | null,
  now: number
): StallReading {
  if (!status || status.checkFailed) {
    return { clock: clock ?? { fingerprint: '', since: now }, stalled: false };
  }

  const fingerprint = stallFingerprint(status);
  if (!clock || clock.fingerprint !== fingerprint) {
    return { clock: { fingerprint, since: now }, stalled: false };
  }

  return { clock, stalled: now - clock.since >= PROCESSING_STALL_MS };
}
